import {
  cancelUserOnboardingSessionApiV1InternalOnboardingSessionsIdCancelPost,
  getSchedulingOptionsApiV1InternalSchedulingOptionsGet,
  listAvailableStylistsApiV1InternalStylistsAvailableGet,
  listScheduledOnboardingSessionsApiV1InternalOnboardingSessionsGet,
  rescheduleUserOnboardingSessionApiV1InternalOnboardingSessionsIdReschedulePost,
  scheduleUserOnboardingSessionApiV1InternalUsersIdOnboardingSessionPost,
  type AdminOnboardingSession,
  type AdminSchedulingOptions,
  type ConsumerSummary,
  type StylistSummary,
} from '@closet-theory/api';
import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from '@untitledui/icons';
import { Button } from '@ui/components/base/buttons/button';
import { Callout } from '@ui/console/primitives';
import { SelectField } from '@ui/console/forms';
import { configureAdminApi, handleUnauthorized } from './auth';
import { ActionDialog } from './components';
import { errorDetail } from './operation-policy';

/** The session kinds that count as the member's session; cancelled ones do not. */
const SESSION_STATUSES = ['draft', 'in_progress', 'completed'] as const;

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function partsInSchedulingTimezone(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return {
    year: Number(value('year')),
    month: Number(value('month')),
    day: Number(value('day')),
    hour: Number(value('hour')),
    minute: Number(value('minute')),
    second: Number(value('second')),
  };
}

function dateKey(date: Date, timezone: string) {
  const { year, month, day } = partsInSchedulingTimezone(date, timezone);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function sessionDate(startsAt: string, timezone: string) {
  return dateKey(new Date(startsAt), timezone);
}

function startsAt(date: string, minutes: number, timezone: string) {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  const targetAsUtc = Date.UTC(year, month - 1, day, Math.floor(minutes / 60), minutes % 60);
  let candidate = targetAsUtc;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const actual = partsInSchedulingTimezone(new Date(candidate), timezone);
    const actualAsUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
    );
    const adjustment = targetAsUtc - actualAsUtc;
    candidate += adjustment;
    if (adjustment === 0) break;
  }
  return new Date(candidate).toISOString();
}

function sessionWhen(scheduledAt: string, timezone: string) {
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(scheduledAt));
}

function slotLabel(minutes: number, durationMinutes: number) {
  const label = (value: number) => {
    const hour = Math.floor(value / 60);
    const minute = value % 60;
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${String(minute).padStart(2, '0')} ${suffix}`;
  };
  return `${label(minutes)}–${label(minutes + durationMinutes)}`;
}

function Calendar({
  value,
  timezone,
  horizonDays,
  onChange,
}: {
  value: string;
  timezone: string;
  horizonDays: number;
  onChange: (value: string) => void;
}) {
  const [yearText = '0', monthText = '0'] = value.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const today = dateKey(new Date(), timezone);
  const horizon = new Date();
  horizon.setUTCDate(horizon.getUTCDate() + horizonDays);
  const finalDate = dateKey(horizon, timezone);
  const previousMonthDisabled = `${year}-${String(month).padStart(2, '0')}` <= today.slice(0, 7);
  const moveMonth = (offset: number) => {
    const target = new Date(Date.UTC(year, month - 1 + offset, 1));
    const nextYear = target.getUTCFullYear();
    const nextMonth = target.getUTCMonth() + 1;
    const nextDay = Math.min(
      Number(value.slice(-2)),
      new Date(Date.UTC(nextYear, nextMonth, 0)).getUTCDate(),
    );
    onChange(
      `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(nextDay).padStart(2, '0')}`,
    );
  };
  const monthLabel = new Intl.DateTimeFormat('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)));

  return (
    <div className="schedule-calendar" aria-label="Session date">
      <div className="schedule-calendar-heading">
        <Button
          size="sm"
          color="tertiary"
          aria-label="Previous month"
          iconLeading={ChevronLeft}
          isDisabled={previousMonthDisabled}
          onClick={() => moveMonth(-1)}
        />
        <strong>{monthLabel}</strong>
        <Button
          size="sm"
          color="tertiary"
          aria-label="Next month"
          iconLeading={ChevronRight}
          onClick={() => moveMonth(1)}
        />
      </div>
      <div className="schedule-calendar-grid">
        {weekdayLabels.map((label) => (
          <span className="schedule-weekday" key={label}>
            {label}
          </span>
        ))}
        {Array.from({ length: firstWeekday }, (_, index) => (
          <span aria-hidden="true" key={`empty-${index}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, index) => {
          const day = index + 1;
          const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const disabled = key < today || key > finalDate;
          return (
            <button
              type="button"
              key={key}
              className={key === value ? 'selected' : ''}
              aria-pressed={key === value}
              disabled={disabled}
              onClick={() => onChange(key)}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ConsumerSchedulingDialog({
  consumer,
  onClose,
  onChanged,
}: {
  consumer: ConsumerSummary;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [options, setOptions] = useState<AdminSchedulingOptions>();
  const [stylists, setStylists] = useState<StylistSummary[]>([]);
  const [existing, setExisting] = useState<AdminOnboardingSession>();
  /** A session already under way or finished, which cannot be moved from here. */
  const [past, setPast] = useState<AdminOnboardingSession>();
  const [stylistId, setStylistId] = useState('');
  const [date, setDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingStylists, setLoadingStylists] = useState(false);
  const [busy, setBusy] = useState<'save' | 'cancel'>();
  const [error, setError] = useState('');
  const [cancelArmed, setCancelArmed] = useState(false);

  useEffect(() => {
    void (async () => {
      configureAdminApi();
      const [optionsResult, ...sessionResults] = await Promise.all([
        getSchedulingOptionsApiV1InternalSchedulingOptionsGet(),
        ...SESSION_STATUSES.map((status) =>
          listScheduledOnboardingSessionsApiV1InternalOnboardingSessionsGet({
            query: { limit: 1, offset: 0, user_id: consumer.id, status },
          }),
        ),
      ]);
      setLoading(false);
      handleUnauthorized(optionsResult.response.status);
      for (const result of sessionResults) handleUnauthorized(result.response.status);
      if (
        optionsResult.error ||
        !optionsResult.data ||
        sessionResults.some((result) => result.error)
      ) {
        setError('Scheduling details could not be loaded.');
        return;
      }
      setOptions(optionsResult.data);
      // Draft first: it is the only kind that can be moved or cancelled.
      const [draft, inProgress, completed] = sessionResults.map((result) => result.data?.items[0]);
      const session = draft;
      setPast(draft ? undefined : (inProgress ?? completed));
      if (session) {
        setExisting(session);
        setDate(sessionDate(session.scheduled_at, optionsResult.data.timezone));
        setSelectedSlot(new Date(session.scheduled_at).toISOString());
      } else {
        const tomorrow = new Date();
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        setDate(dateKey(tomorrow, optionsResult.data.timezone));
      }
    })();
  }, [consumer.id]);

  useEffect(() => {
    setStylistId('');
    setStylists([]);
    if (!selectedSlot || !options) {
      return;
    }
    let current = true;
    void (async () => {
      configureAdminApi();
      setLoadingStylists(true);
      setError('');
      const result = await listAvailableStylistsApiV1InternalStylistsAvailableGet({
        query: {
          scheduled_at: selectedSlot,
          timezone: options.timezone,
          duration_minutes: options.duration_minutes,
          ...(existing ? { exclude_session_id: existing.id } : {}),
        },
      });
      if (!current) return;
      setLoadingStylists(false);
      handleUnauthorized(result.response.status);
      if (result.error) {
        setStylists([]);
        setError(
          `${errorDetail(result.error, 'Available Stylists could not be loaded.')} Choose the time again or select another window to retry.`,
        );
        return;
      }
      const available = result.data?.items ?? [];
      setStylists(available);
      const requiredStylist = existing?.stylist_id ?? consumer.assigned_stylist_id;
      if (requiredStylist && available.some((stylist) => stylist.id === requiredStylist)) {
        setStylistId(requiredStylist);
      }
    })();
    return () => {
      current = false;
    };
  }, [consumer.assigned_stylist_id, existing, options, selectedSlot]);

  const requiredStylist = existing?.stylist_id ?? consumer.assigned_stylist_id;
  const requiredStylistName =
    (existing ? existing.stylist_name : consumer.assigned_stylist_name) ?? 'Their stylist';
  const requiredStylistBusy = Boolean(
    requiredStylist &&
    selectedSlot &&
    !loadingStylists &&
    !error &&
    !stylists.some((stylist) => stylist.id === requiredStylist),
  );
  const underway = past?.status === 'in_progress';
  const stylistHint = requiredStylistBusy
    ? `${requiredStylistName} is not free at this time. Pick another time.`
    : existing
      ? 'Cancel the current session before changing its stylist.'
      : consumer.assigned_stylist_id
        ? 'Sessions are booked with the member’s assigned stylist.'
        : undefined;

  const windows = useMemo(
    () =>
      (options?.slot_start_minutes ?? []).map((minutes) => {
        const starts_at = startsAt(date, minutes, options?.timezone ?? 'UTC');
        return { minutes, starts_at, blocked: new Date(starts_at).getTime() <= Date.now() };
      }),
    [date, options],
  );

  const save = async () => {
    if (!options || !stylistId || !selectedSlot) return;
    setBusy('save');
    setError('');
    configureAdminApi();
    const result = existing
      ? await rescheduleUserOnboardingSessionApiV1InternalOnboardingSessionsIdReschedulePost({
          path: { id: existing.id },
          body: {
            expected_version: existing.version,
            scheduled_at: selectedSlot,
            duration_minutes: options.duration_minutes,
            timezone: options.timezone,
            idempotency_key: `admin-reschedule:${crypto.randomUUID()}`,
            reason: 'Admin rescheduled onboarding session from Consumers',
          },
        })
      : await scheduleUserOnboardingSessionApiV1InternalUsersIdOnboardingSessionPost({
          path: { id: consumer.id },
          body: {
            stylist_id: stylistId,
            expected_assigned_stylist_id: consumer.assigned_stylist_id ?? null,
            scheduled_at: selectedSlot,
            duration_minutes: options.duration_minutes,
            timezone: options.timezone,
            idempotency_key: `admin-schedule:${crypto.randomUUID()}`,
            reason: 'Admin scheduled onboarding session from Consumers',
          },
        });
    setBusy(undefined);
    handleUnauthorized(result.response.status);
    if (result.error) {
      setError(errorDetail(result.error, 'The session could not be saved.'));
      return;
    }
    await onChanged();
    onClose();
  };

  const cancel = async () => {
    if (!existing) return;
    setBusy('cancel');
    setError('');
    configureAdminApi();
    const result = await cancelUserOnboardingSessionApiV1InternalOnboardingSessionsIdCancelPost({
      path: { id: existing.id },
      body: {
        expected_version: existing.version,
        idempotency_key: `admin-cancel:${crypto.randomUUID()}`,
        reason: 'Admin cancelled onboarding session from Consumers',
      },
    });
    setBusy(undefined);
    handleUnauthorized(result.response.status);
    if (result.error) {
      setError(errorDetail(result.error, 'The session could not be cancelled.'));
      return;
    }
    await onChanged();
    onClose();
  };

  return (
    <ActionDialog
      title={`${existing ? 'Edit session' : 'Schedule session'} for ${consumer.name}`}
      description="Pick a date, a start time and a stylist who is free. Confirming books the session and emails the member."
      onClose={onClose}
      footer={
        <>
          {existing ? (
            <Button
              size="md"
              color="primary-destructive"
              isDisabled={Boolean(busy)}
              onClick={() => {
                if (cancelArmed) void cancel();
                else setCancelArmed(true);
              }}
            >
              {busy === 'cancel'
                ? 'Cancelling…'
                : cancelArmed
                  ? 'Confirm cancel session'
                  : 'Cancel session'}
            </Button>
          ) : null}
          <Button
            size="md"
            color="primary"
            isDisabled={loading || underway || Boolean(busy) || !stylistId || !selectedSlot}
            onClick={() => void save()}
          >
            {busy === 'save' ? 'Saving…' : existing ? 'Reschedule session' : 'Schedule session'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-tertiary">
        {options
          ? `Sessions run ${options.duration_minutes} minutes. All times shown are ${options.timezone}.`
          : 'Loading scheduling policy…'}
      </p>
      {options && existing ? (
        <Callout
          tone="neutral"
          title={`Booked for ${sessionWhen(existing.scheduled_at, options.timezone)} with ${existing.stylist_name ?? 'no named stylist'}.`}
        >
          Pick a new time below to move it, or cancel it.
        </Callout>
      ) : null}
      {options && past ? (
        <Callout
          tone={underway ? 'warning' : 'neutral'}
          title={
            underway
              ? `A session is under way: ${sessionWhen(past.scheduled_at, options.timezone)} with ${past.stylist_name ?? 'no named stylist'}.`
              : `Their last session, ${sessionWhen(past.scheduled_at, options.timezone)} with ${past.stylist_name ?? 'no named stylist'}, is completed.`
          }
        >
          {underway
            ? 'It cannot be moved or cancelled here, and another one cannot be booked until it finishes.'
            : 'You can book another session below.'}
        </Callout>
      ) : null}
      {cancelArmed ? (
        <Callout tone="warning" title="Press again to cancel this session.">
          This removes the current booking. The member will need another session scheduled later.
        </Callout>
      ) : null}
      {error ? <Callout tone="error" title={error} /> : null}
      {loading ? (
        <p aria-live="polite">Loading scheduling details…</p>
      ) : options ? (
        <>
          <Calendar
            value={date}
            timezone={options.timezone}
            horizonDays={options.horizon_days}
            onChange={(value) => {
              setDate(value);
              setSelectedSlot('');
              setStylistId('');
            }}
          />
          <div>
            <div className="schedule-slot-heading">
              <strong>Requested time</strong>
              <span>Sessions can start every {options.slot_interval_minutes} minutes</span>
            </div>
            <div className="schedule-slot-slider" aria-label="Available one-hour time windows">
              {windows.map(({ minutes, starts_at, blocked }) => (
                <button
                  type="button"
                  key={minutes}
                  disabled={blocked}
                  className={starts_at === selectedSlot ? 'selected' : ''}
                  aria-pressed={starts_at === selectedSlot}
                  onClick={() => {
                    setSelectedSlot(starts_at);
                    setStylistId('');
                  }}
                >
                  {slotLabel(minutes, options.duration_minutes)}
                </button>
              ))}
            </div>
          </div>
          <SelectField
            label="Available Stylist"
            value={stylistId}
            disabled={
              !selectedSlot || loadingStylists || Boolean(existing || consumer.assigned_stylist_id)
            }
            onChange={(event) => setStylistId(event.currentTarget.value)}
            {...(stylistHint ? { hint: stylistHint } : {})}
            options={[
              {
                value: '',
                label: !selectedSlot
                  ? 'Choose a date and time first'
                  : loadingStylists
                    ? 'Checking availability…'
                    : stylists.length
                      ? 'Choose an available Stylist'
                      : 'No Stylist is available',
              },
              ...stylists.map((stylist) => ({ value: stylist.id, label: stylist.name })),
            ]}
          />
        </>
      ) : null}
    </ActionDialog>
  );
}
